import { type VolunteerLocationItemDto } from '@sola/skippy-search-sdk/lib/schema.ts';
import {
  doDateRangesOverlap,
  formatDate,
  formatDateRange,
  formatEventDate,
  formatSlotDate,
  formatTime,
} from '@sola/utils/date.ts';

// ── Types ────────────────────────────────────────────────────────────────────

export interface VolunteerRoleRules {
  minAge?: 14 | 16 | 18;
}

export interface VolunteerRoleConfig {
  labelKey: string;
  rules: VolunteerRoleRules;
  contentPosition?: string;
}

export interface VolunteerSlot {
  id: string;
  eventLocationId: string;
  startDate: string;
  endDate: string;
  status: string;
  description: string | null;
  remainingCapacity: number;
}

/** Maps contactId → array of slotIds they are assigned to. */
export type ShiftAssignments = Record<string, string[]>;

// ── Contact types ────────────────────────────────────────────────────────────

export type GuestAgeRange = '14-15' | '16-17' | '18+';

export const isMinor = (ageRange: GuestAgeRange): boolean => ageRange !== '18+';

const ageRangeMinAge: Record<GuestAgeRange, number> = {
  '14-15': 14,
  '16-17': 16,
  '18+': 18,
};

export const meetsAgeRequirement = (
  ageRange: GuestAgeRange,
  minAge: number,
): boolean => ageRangeMinAge[ageRange] >= minAge;

export type GuestRelationship =
  | 'self'
  | 'child'
  | 'nonresident-child'
  | 'spouse'
  | 'friend'
  | 'relative'
  | 'other';

const guestRelationshipApiLabel: Record<GuestRelationship, string> = {
  self: 'Self',
  child: 'Resident Child',
  'nonresident-child': 'Nonresident Child',
  spouse: 'Spouse',
  friend: 'Friend',
  relative: 'Relative',
  other: 'Other',
};

export const getRelationshipApiLabel = (
  relationship: GuestRelationship,
): string => guestRelationshipApiLabel[relationship];

export interface GuestContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  ageRange: GuestAgeRange;
  relationship: GuestRelationship;
}

export interface KnownContact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  email: string | null;
  phone: string | null;
}

export type VolunteerContact = KnownContact | GuestContact;

// ── Shared type guards & helpers ─────────────────────────────────────────────

/** Type guard: narrows VolunteerContact to GuestContact. */
export const isGuestContact = (c: VolunteerContact): c is GuestContact =>
  'relationship' in c;

/** Formats a contact's display name from first/last name parts. */
export const getContactDisplayName = (contact: {
  firstName: string | null;
  lastName: string | null;
}): string => [contact.firstName, contact.lastName].filter(Boolean).join(' ');

// ── Slot grouping key ────────────────────────────────────────────────────────

/** Slots with the same key represent interchangeable positions. */
export const getSlotGroupKey = (slot: {
  description?: string | null;
  startDate: string;
  endDate: string;
}): string =>
  `${slot.description ?? 'General'}__${slot.startDate}__${slot.endDate}`;

// ── Registration helpers ─────────────────────────────────────────────────────

/** Tracks slot group keys (name__start__end) that were previously registered per contact. */
export type PreviousRegistrations = Record<string, string[]>;

/**
 * Builds a map of previous registrations from already-registered slots.
 * Returns the registrations map and a Set of previously registered contact IDs.
 */
export const buildPreviousRegistrations = (
  slots: Array<{
    contactId: string;
    shiftName?: string | null;
    startDate: string;
    endDate: string;
  }>,
): {
  previousRegistrations: PreviousRegistrations;
  previouslyRegisteredContactIds: Set<string>;
} => {
  const previousRegistrations: PreviousRegistrations = {};
  const previouslyRegisteredContactIds = new Set<string>();

  for (const slot of slots) {
    if (!previousRegistrations[slot.contactId]) {
      previousRegistrations[slot.contactId] = [];
    }
    const groupKey = getSlotGroupKey({
      description: slot.shiftName,
      startDate: slot.startDate,
      endDate: slot.endDate,
    });
    previousRegistrations[slot.contactId].push(groupKey);
    previouslyRegisteredContactIds.add(slot.contactId);
  }

  return { previousRegistrations, previouslyRegisteredContactIds };
};

// ── Media helpers ────────────────────────────────────────────────────────────

/** Builds the media prop for a volunteer location image, or undefined when no image is available. */
export const buildLocationMedia = (
  location: Pick<VolunteerLocationItemDto, 'image' | 'locationName'>,
) =>
  location.image
    ? {
        image: {
          url: location.image.url,
          alt: location.locationName,
          width: 700,
          height: 467,
        },
      }
    : undefined;

// ── Display formatting ───────────────────────────────────────────────────────

/**
 * Format a volunteer event's date range for display.
 * Treats dates as wall-clock times (volunteer dates have no timezone offset).
 */
export const formatEventDateRange = (
  volunteer: Pick<
    VolunteerLocationItemDto,
    'startDate' | 'endDate' | 'tourEvents'
  >,
  isTour: boolean,
  variant: 'default' | 'card' | 'event' = 'default',
): string => {
  const startDate = new Date(volunteer.startDate);

  // Event detail page:
  // Single day: "Thu • March 12, 2026 • 7:00 PM"
  // Multi-day:  "Thu, March 12 – Sat, March 14, 2026"
  if (variant === 'event' && !isTour) {
    return formatEventDate(volunteer.startDate, volunteer.endDate);
  }

  // Event card: "Sat, Mar 15 • 7:00 PM"
  if (variant === 'card' && !isTour) {
    const shortDate = formatDate(startDate, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `${shortDate} \u2022 ${formatTime(startDate)}`;
  }

  // Default: date range
  const tourEvents = volunteer.tourEvents ?? [];
  const lastEndDate = isTour
    ? tourEvents.reduce(
        (max, e) => (e?.endDate && e.endDate > max ? e.endDate : max),
        volunteer.endDate,
      )
    : volunteer.endDate;

  return formatDateRange(volunteer.startDate, lastEndDate, {
    monthOnly: isTour,
  });
};

/**
 * Format a YYYY-MM-DD slot date-key for display (UTC, since date-keys are calendar dates).
 * Wraps formatSlotDate with utc:true baked in so callers don't repeat the option.
 */
export const formatSlotDateKey = (
  dateKey: string,
  style: 'long' | 'short' = 'long',
  includeYear = false,
  locale = 'en-US',
): string => formatSlotDate(dateKey, style, includeYear, locale, { utc: true });

/**
 * Build a location display string, optionally showing tour location count.
 */
export const formatLocationDisplay = (
  volunteer: Pick<VolunteerLocationItemDto, 'city' | 'state' | 'tourEvents'>,
  isTour: boolean,
  isTourList: boolean,
): string => {
  const tourEvents = volunteer.tourEvents ?? [];
  const uniqueTourLocations = isTour
    ? [...new Set(tourEvents.map((e) => `${e.city}, ${e.state}`))]
    : [];
  const otherLocationCount = uniqueTourLocations.length - 1;

  if (isTour && !isTourList && otherLocationCount > 0) {
    return `${volunteer.city}, ${volunteer.state} + ${otherLocationCount} other location${otherLocationCount > 1 ? 's' : ''}`;
  }

  return `${volunteer.city}, ${volunteer.state}`;
};

// ── Role matching & filtering ────────────────────────────────────────────────

/** Keys match slot `description` from the API. */
export const volunteerRoleConfigs: Record<string, VolunteerRoleConfig> = {
  'Table Volunteer': {
    labelKey: 'roles.tableVolunteer',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleTable',
  },
  Greeter: {
    labelKey: 'roles.greeter',
    rules: {},
    contentPosition: 'volunteerRoleGreeter',
  },
  'Event Facilitator': {
    labelKey: 'roles.eventFacilitator',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleEventFacilitator',
  },
  'Compassion Experience Volunteer': {
    labelKey: 'roles.compassionExperience',
    rules: { minAge: 14 },
    contentPosition: 'volunteerRoleCompEx',
  },
  'Compassion Experience Setup Volunteer': {
    labelKey: 'roles.setUp',
    rules: { minAge: 18 },
    contentPosition: 'volunteerRoleSetUpTearDown',
  },
  'CompEx Teardown Volunteer': {
    labelKey: 'roles.tearDown',
    rules: { minAge: 18 },
    contentPosition: 'volunteerRoleSetUpTearDown',
  },
  'Handout Volunteer': {
    labelKey: 'roles.handoutVolunteer',
    rules: { minAge: 14 },
    contentPosition: 'volunteerRoleHandout',
  },
  General: {
    labelKey: 'roles.general',
    rules: {},
    contentPosition: 'volunteerRoleGeneral',
  },
  'Event Volunteer': {
    labelKey: 'roles.eventVolunteer',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleEventVolunteer',
  },
  'VR Experience Volunteer': {
    labelKey: 'roles.vrExperience',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleVR',
  },
  'Volunteer Entrance Host': {
    labelKey: 'roles.entranceHost',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleEntranceHost',
  },
  'Set-Up Volunteer': {
    labelKey: 'roles.setUp',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleSetUpTearDown',
  },
  'Tear-Down Volunteer': {
    labelKey: 'roles.tearDown',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleSetUpTearDown',
  },
  'Setup/Teardown Volunteer': {
    labelKey: 'roles.setUpTearDown',
    rules: { minAge: 16 },
    contentPosition: 'volunteerRoleSetUpTearDown',
  },
};

const defaultVolunteerRole = 'General';
const defaultRoleConfig: VolunteerRoleConfig = {
  labelKey: 'roles.general',
  rules: {},
};

export const getRoleConfig = (
  description: string | null,
): VolunteerRoleConfig =>
  volunteerRoleConfigs[description ?? defaultVolunteerRole] ??
  defaultRoleConfig;

/**
 * Normalize a role name for fuzzy matching.
 * Lowercases, strips hyphens, normalizes compound words, and collapses whitespace.
 */
export const normalizeRoleName = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replaceAll('-', ' ')
    .replaceAll('/', ' ')
    .replaceAll('setup', 'set up')
    .replaceAll('teardown', 'tear down')
    .replaceAll(/\s+/g, ' ');

/** Core matching logic for a single title against role counts. */
const findMatchingSingle = (
  title: string,
  roleCounts: { role: string; openSlots: number; totalSlots: number }[],
): { role: string; openSlots: number; totalSlots: number } | undefined => {
  const titleNorm = normalizeRoleName(title);

  // Exact match first (highest priority)
  const exact = roleCounts.find(
    (rc) => normalizeRoleName(rc.role) === titleNorm,
  );
  if (exact) {
    return exact;
  }

  // Substring match
  const substring = roleCounts.find((rc) => {
    const roleNorm = normalizeRoleName(rc.role);
    return roleNorm.includes(titleNorm) || titleNorm.includes(roleNorm);
  });
  if (substring) {
    return substring;
  }

  // Prefix fallback: if title starts with "event", match any role starting with "event"
  const titlePrefix = titleNorm.split(' ')[0];
  if (titlePrefix === 'event') {
    return roleCounts.find((rc) =>
      normalizeRoleName(rc.role).startsWith('event'),
    );
  }

  return undefined;
};

/**
 * Match a content item title to a role slot count using fuzzy name matching.
 * Falls back to prefix matching (e.g. "Event Volunteer" matches "Event Facilitator").
 */
export const findMatchingRole = (
  title: string,
  roleCounts: { role: string; openSlots: number; totalSlots: number }[],
): { role: string; openSlots: number; totalSlots: number } | undefined => {
  return findMatchingSingle(title, roleCounts);
};

/**
 * Find ALL role slot counts that match a content item title.
 * Used for combined roles (e.g. "Setup/Teardown Volunteer" should aggregate
 * slots from both "Set-Up Volunteer" and "Tear-Down Volunteer").
 *
 * Matching tiers (a role is included if ANY tier matches):
 * 1. Exact normalized match
 * 2. Bidirectional substring containment
 * 3. Component matching — for compound titles (containing multiple words before
 *    "volunteer"), each component word group is checked against roles individually
 *    (e.g. "set up tear down volunteer" matches "set up volunteer" and "tear down volunteer")
 * 4. Event-prefix fallback — if title starts with "event", include any role
 *    that also starts with "event"
 */
export const findAllMatchingRoles = (
  title: string,
  roleCounts: { role: string; openSlots: number; totalSlots: number }[],
): { role: string; openSlots: number; totalSlots: number }[] => {
  const titleNorm = normalizeRoleName(title);

  const matches = roleCounts.filter((rc) => {
    const roleNorm = normalizeRoleName(rc.role);

    // Tier 1: exact match
    if (roleNorm === titleNorm) {
      return true;
    }

    // Tier 2: bidirectional substring
    if (roleNorm.includes(titleNorm) || titleNorm.includes(roleNorm)) {
      return true;
    }

    // Tier 3: component matching for compound titles
    // Split the title into word groups separated by known role suffixes (e.g. "volunteer")
    // "set up tear down volunteer" → components ["set up", "tear down"]
    const suffix = 'volunteer';
    const titleWithoutSuffix = titleNorm.endsWith(suffix)
      ? titleNorm.slice(0, -suffix.length).trim()
      : titleNorm;
    const roleWithoutSuffix = roleNorm.endsWith(suffix)
      ? roleNorm.slice(0, -suffix.length).trim()
      : roleNorm;

    // Check if the role's core (without suffix) is a component of the title
    if (
      titleWithoutSuffix.length > roleWithoutSuffix.length &&
      roleWithoutSuffix.length > 0
    ) {
      // Split title core into words and check if the role core words appear
      // as a contiguous sequence
      const titleWords = titleWithoutSuffix.split(' ');
      const roleWords = roleWithoutSuffix.split(' ');
      for (let i = 0; i <= titleWords.length - roleWords.length; i++) {
        const slice = titleWords.slice(i, i + roleWords.length);
        if (slice.join(' ') === roleWords.join(' ')) {
          return true;
        }
      }
    }

    // Tier 3b: setup/teardown keyword matching
    // A compound title containing "set up" and/or "tear down" should match
    // any role that also contains one of those keywords (regardless of prefixes
    // like "compex" or "compassion experience")
    const setupTeardownKeywords = ['set up', 'tear down'];
    const titleKeywords = setupTeardownKeywords.filter((kw) =>
      titleNorm.includes(kw),
    );
    if (titleKeywords.length > 0) {
      const roleHasKeyword = setupTeardownKeywords.some((kw) =>
        roleNorm.includes(kw),
      );
      if (roleHasKeyword) {
        return true;
      }
    }

    return false;
  });

  // Tier 4: event-prefix fallback (only when no matches found above)
  if (matches.length === 0) {
    const titlePrefix = titleNorm.split(' ')[0];
    if (titlePrefix === 'event') {
      return roleCounts.filter((rc) =>
        normalizeRoleName(rc.role).startsWith('event'),
      );
    }
  }

  return matches;
};

/**
 * Roles that should only be shown when they have available slots.
 * All other roles are always visible regardless of slot availability.
 */
const conditionalRoles = new Set([
  'handout volunteer',
  'compassion experience volunteer',
  'vr experience volunteer',
  'volunteer entrance host',
]);

/**
 * Determine if a role item should be visible.
 * Conditional roles are hidden when they have no open slots.
 */
export const shouldShowRole = (
  title: string,
  roleCounts:
    | { role: string; openSlots: number; totalSlots: number }[]
    | undefined,
): boolean => {
  const titleNorm = normalizeRoleName(title);
  if (!conditionalRoles.has(titleNorm)) {
    return true;
  }
  if (!roleCounts) {
    return true; // No data yet — show all until we know
  }
  const match = findMatchingRole(title, roleCounts);
  return !!match && match.openSlots > 0;
};

/**
 * Check if a content item title matches any role in a list of role names.
 * Uses the same fuzzy matching as findMatchingRole (normalize + includes + event prefix).
 */
export const hasMatchingRole = (title: string, roles: string[]): boolean => {
  const asCounts = roles.map((r) => ({ role: r, openSlots: 0, totalSlots: 0 }));
  return !!findMatchingRole(title, asCounts);
};

/**
 * Enrich role content items with live slot count badges, or filter by indexed roles.
 *
 * When `roleCounts` is available (live data loaded), filters to roles with open slots
 * and attaches a badge showing the count.
 *
 * When `roleCounts` is not yet available, falls back to filtering items by the
 * indexed `roles` array (no badge).
 */
export const buildFilteredRoleItems = <T extends { title?: string | null }>(
  items: T[] | undefined,
  roleCounts:
    | { role: string; openSlots: number; totalSlots: number }[]
    | undefined,
  indexedRoles: string[],
  formatBadgeLabel: (openSlots: number) => string,
): (T & { badge?: { label: string; color: string; size: string } })[] => {
  if (!items) {
    return [];
  }

  if (roleCounts) {
    return items
      .filter((item) => shouldShowRole(item.title ?? '', roleCounts))
      .map((item) => {
        const allMatches = findAllMatchingRoles(item.title ?? '', roleCounts);
        if (allMatches.length === 0) {
          return null;
        }
        const combinedOpenSlots = allMatches.reduce(
          (sum, m) => sum + m.openSlots,
          0,
        );
        return {
          ...item,
          badge: {
            label: formatBadgeLabel(combinedOpenSlots),
            color: 'brand',
            size: 'sm',
          },
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }

  return items
    .filter((item) => hasMatchingRole(item.title ?? '', indexedRoles))
    .map((item) => ({ ...item }));
};

// ── Slot grouping & deduplication ────────────────────────────────────────────

interface RawSlot {
  id: string;
  eventLocationId: string;
  startDate: string;
  endDate: string;
  status: string;
  description?: string | null;
}

/**
 * Extract the date portion from an ISO-ish datetime string (e.g. "2026-09-26").
 * Works with both naive local times and UTC strings.
 */
export const getSlotDateKey = (isoString: string): string =>
  isoString.slice(0, 10);

/**
 * Collapses available slots with the same role + time into grouped entries.
 * `remainingCapacity` reflects how many individual slots remain.
 */
export const deduplicateSlots = (rawSlots: RawSlot[]): VolunteerSlot[] => {
  const available = rawSlots.filter((s) => s.status === 'Available');

  const groupMap = new Map<string, { ids: string[]; slot: RawSlot }>();
  for (const slot of available) {
    const key = getSlotGroupKey(slot);
    const existing = groupMap.get(key);
    if (existing) {
      existing.ids.push(slot.id);
    } else {
      groupMap.set(key, { ids: [slot.id], slot });
    }
  }

  return [...groupMap.values()].map(({ ids, slot }) => ({
    id: ids[0],
    eventLocationId: slot.eventLocationId,
    startDate: slot.startDate,
    endDate: slot.endDate,
    status: slot.status,
    description: slot.description ?? null,
    remainingCapacity: ids.length,
  }));
};

/** Maps each slot group key to all available raw slot IDs in that group. */
export const buildSlotGroupIds = (
  rawSlots: RawSlot[],
): Map<string, string[]> => {
  const available = rawSlots.filter((s) => s.status === 'Available');
  const pool = new Map<string, string[]>();
  for (const slot of available) {
    const key = getSlotGroupKey(slot);
    const ids = pool.get(key) ?? [];
    ids.push(slot.id);
    pool.set(key, ids);
  }
  return pool;
};

/** Groups slots by role label, sorted by start time within each group. */
export const groupSlotsByRole = (
  slots: VolunteerSlot[],
  fallbackLabel: string,
): Array<[string, VolunteerSlot[]]> => {
  const byRole: Record<string, VolunteerSlot[]> = {};
  for (const slot of slots) {
    const key = slot.description ?? fallbackLabel;
    const group = byRole[key] ?? [];
    group.push(slot);
    byRole[key] = group;
  }
  for (const group of Object.values(byRole)) {
    group.sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    );
  }
  return Object.entries(byRole);
};

/**
 * Group a sorted array of slots by their date. Returns entries in chronological order.
 * Only meaningful for multi-day events — single-day events return a single group.
 */
export const groupSlotsByDate = (
  slots: VolunteerSlot[],
): Array<[string, VolunteerSlot[]]> => {
  const byDate: Record<string, VolunteerSlot[]> = {};
  for (const slot of slots) {
    const dateKey = getSlotDateKey(slot.startDate);
    const group = byDate[dateKey] ?? [];
    group.push(slot);
    byDate[dateKey] = group;
  }
  return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b));
};

// ── Conflict detection & capacity ────────────────────────────────────────────

/** Checks whether a contact has a time conflict with the given slot. */
export const hasSlotTimeConflict = (
  contactId: string,
  targetSlot: VolunteerSlot,
  allSlots: VolunteerSlot[],
  assignments: ShiftAssignments,
  slotGroupIds: Map<string, string[]>,
): boolean => {
  const contactAssignedIds = assignments[contactId] ?? [];
  const assignedSlots = allSlots.filter((s) => {
    const groupIds = slotGroupIds.get(getSlotGroupKey(s)) ?? [];
    return (
      s.id !== targetSlot.id &&
      contactAssignedIds.some((id) => groupIds.includes(id))
    );
  });
  return assignedSlots.some((s) =>
    doDateRangesOverlap(
      s.startDate,
      s.endDate,
      targetSlot.startDate,
      targetSlot.endDate,
    ),
  );
};

/** Checks whether a slot's capacity is exhausted for a specific contact. */
export const isSlotAtCapacity = (
  contactId: string,
  slot: VolunteerSlot,
  pool: string[],
  assignments: ShiftAssignments,
): boolean => {
  const othersAssigned = new Set(
    Object.entries(assignments)
      .filter(([cId]) => cId !== contactId)
      .flatMap(([, ids]) => ids),
  );
  const takenByOthers = pool.filter((id) => othersAssigned.has(id)).length;
  return takenByOthers >= slot.remainingCapacity;
};
